import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Phone, Mail, Target, Ruler, Weight, Calendar, User, Dumbbell } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import ComingSoon from "@/components/ComingSoon";
import Measurements from "@/components/Measurements";
import WorkoutPrograms from "@/components/WorkoutPrograms";
import AppointmentItem from "@/components/AppointmentItem";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function MemberDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);

  useEffect(() => { api.get(`/members/${id}`).then((r) => setM(r.data)).catch(() => navigate(-1)); }, [id]);

  if (!m) return <Skeleton className="h-96 rounded-2xl bg-[#13131C]" />;

  return (
    <div data-testid="member-detail-page">
      <Button variant="ghost" onClick={() => navigate(-1)} className="text-[#A0A0B2] mb-4 -ml-2"><ArrowLeft size={18} className="mr-1" /> Geri</Button>

      <div className="rounded-2xl bg-gradient-to-br from-[#181824] to-[#13131C] border border-[#222230] p-6 mb-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <Avatar className="w-20 h-20"><AvatarFallback className="bg-[#FF5500] text-white text-2xl font-bold">{m.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}</AvatarFallback></Avatar>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display font-extrabold text-2xl text-white">{m.name}</h1>
              <StatusBadge status={m.status} />
            </div>
            <div className="flex items-center gap-4 mt-2 text-sm text-[#A0A0B2] flex-wrap">
              <span className="flex items-center gap-1.5"><Mail size={14} /> {m.email}</span>
              <span className="flex items-center gap-1.5"><Phone size={14} /> {m.phone || "—"}</span>
            </div>
          </div>
          {m.remaining_lessons != null && (
            <div className="text-center bg-[#FF5500]/10 border border-[#FF5500]/30 rounded-2xl px-6 py-3">
              <p className="font-mono font-extrabold text-3xl text-[#FF5500]">{m.remaining_lessons}</p>
              <p className="text-xs text-[#A0A0B2]">kalan ders</p>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
        <Info icon={Target} label="Hedef" value={m.goal || "—"} />
        <Info icon={User} label="Cinsiyet" value={m.gender || "—"} />
        <Info icon={Ruler} label="Boy" value={m.height ? `${m.height} cm` : "—"} />
        <Info icon={Weight} label="Kilo" value={m.weight ? `${m.weight} kg` : "—"} />
        <Info icon={Dumbbell} label="Antrenör" value={m.trainer_name || "—"} />
        <Info icon={Calendar} label="Paket" value={m.package_name || "—"} />
      </div>

      <Tabs defaultValue="workout">
        <TabsList className="bg-[#13131C] border border-[#222230]">
          <TabsTrigger value="workout" className="data-[state=active]:bg-[#FF5500] data-[state=active]:text-white">Antrenman</TabsTrigger>
          <TabsTrigger value="measurements" className="data-[state=active]:bg-[#FF5500] data-[state=active]:text-white">Ölçümler</TabsTrigger>
          <TabsTrigger value="appointments" className="data-[state=active]:bg-[#FF5500] data-[state=active]:text-white">Randevular</TabsTrigger>
        </TabsList>
        <TabsContent value="workout"><WorkoutPrograms memberId={m.id} canEdit /></TabsContent>
        <TabsContent value="measurements"><Measurements memberId={m.id} canEdit /></TabsContent>
        <TabsContent value="appointments"><ApptHistory memberId={m.id} /></TabsContent>
      </Tabs>
    </div>
  );
}

function ApptHistory({ memberId }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get(`/appointments?member_id=${memberId}`).then((r) => setItems(r.data)).catch(() => setItems([])); }, [memberId]);
  if (items === null) return <div className="py-10 text-center text-[#68687B] text-sm">Yükleniyor...</div>;
  if (items.length === 0) return <ComingSoon title="Randevu Geçmişi" phase={2} compact />;
  return <div className="space-y-2 mt-4">{items.map((a) => <AppointmentItem key={a.id} appt={a} showMember={false} />)}</div>;
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-[#13131C] border border-[#222230] p-4">
      <div className="flex items-center gap-1.5 text-[#68687B] mb-1"><Icon size={14} /><span className="text-xs">{label}</span></div>
      <p className="text-sm font-semibold text-white truncate">{value}</p>
    </div>
  );
}
